import { useState, useEffect, useMemo, useRef, lazy, Suspense } from 'react'
import { useAuthStore } from '../stores/authStore'
import api from '../services/api'
import { useOnlineUsers } from '../hooks/useOnlineUsers'
import { useFillViewportHeight } from '../hooks/useFillViewportHeight'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import ImportExportJobsPanel from '../components/admin/ImportExportJobsPanel'
import QuestionImportModal from '../components/admin/QuestionImportModal'
import OrganizationFormModal from '../components/admin/OrganizationFormModal'
import OrgMemberManager from '../components/admin/OrgMemberManager'
import organizationAdminService from '../services/organizationAdminService'
import SchoolImportModal from '../components/admin/SchoolImportModal'
import CloneUserModal from '../components/admin/users/CloneUserModal'
import {
  Users,
  Shield,
  ShieldCheck,
  UserX,
  UserCheck,
  GraduationCap,
  AlertTriangle,
  Clock,
  Database,
  Upload,
  RefreshCw,
  CheckCircle,
  Download,
  UserPlus,
  Mail,
  Calendar,
  Video,
  Copy,
  Check,
  CheckSquare,
  Square,
  Trash2,
  Settings,
  MessageSquare,
  Building2,
  Eye,
  X,
  School,
  FileQuestion,
  Gamepad2,
  ClipboardList,
  Search,
  ChevronLeft,
  ChevronRight,
  Network,
  Target,
  Award,
  TrendingUp,
  Activity,
  Trophy,
  Wifi,
  Send,
  Gift,
  CreditCard,
  Flag,
  Tag,
  Sparkles,
  Archive,
  Megaphone,
  Film,
  Palette
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import ChallengeInvitationLink from '../components/ChallengeInvitationLink'
import ConfirmDialog from '../components/ConfirmDialog'
import UserDetailsModal from '../components/admin/UserDetailsModal'
import OnlineUsersModal from '../components/admin/OnlineUsersModal'
import toast from 'react-hot-toast'
import { getErrorMessage } from '../utils/errorUtils.js'
import { useDebounce } from '../hooks/useDebounce'
import { createLogger } from '../utils/logger'

const logger = createLogger('AdminDashboard')

// Admin tab panels are lazy-loaded so each tab ships as its own JS chunk and only loads
// when that tab is opened, instead of bundling all ~28 tabs into one eager admin chunk
// (ADMIN-2). The active tab is rendered inside a <Suspense> boundary below. Modals and
// helper panels used inside the always-rendered inline tabs stay eager (imported above).
const AdminFeedbackDashboard = lazy(() => import('./AdminFeedbackDashboard'))
const ComplianceAdminPanel = lazy(() => import('../components/admin/ComplianceAdminPanel'))
const VaultsTab = lazy(() => import('../components/admin/VaultsTab'))
const QuestionsTab = lazy(() => import('../components/admin/QuestionsTab'))
const ChallengeTypesTab = lazy(() => import('../components/admin/ChallengeTypesTab'))
const KnowledgeGraphTab = lazy(() => import('../components/admin/KnowledgeGraphTab'))
const WaitlistTab = lazy(() => import('../components/admin/WaitlistTab'))
const AuditLogTab = lazy(() => import('../components/admin/AuditLogTab'))
const JobsTab = lazy(() => import('../components/admin/JobsTab'))
const EmailManagementTab = lazy(() =>
  import('../components/admin/email').then((m) => ({ default: m.EmailManagementTab }))
)
const QueueMonitorTab = lazy(() => import('../components/admin/QueueMonitorTab'))
const FomoConfigTab = lazy(() => import('../components/admin/FomoConfigTab'))
const AIAdvisorTab = lazy(() => import('../components/admin/AIAdvisorTab'))
const WhitelabelingTab = lazy(() => import('../components/admin/WhitelabelingTab'))
const ReferralsTab = lazy(() => import('../components/admin/ReferralsTab'))
const CampaignsTab = lazy(() =>
  import('../components/admin/campaigns').then((m) => ({ default: m.CampaignsTab }))
)
const DatabaseQueryTab = lazy(() => import('../components/admin/DatabaseQueryTab'))
const LicensingTab = lazy(() => import('../components/admin/licensing/LicensingTab'))
const FeatureFlagsTab = lazy(() => import('../components/admin/FeatureFlagsTab'))
const SchoolsTab = lazy(() => import('../components/admin/SchoolsTab'))
const CoachApplicationsTab = lazy(() => import('../components/admin/CoachApplicationsTab'))
const EventsTab = lazy(() => import('../components/admin/EventsTab'))
const LiveSessionRecordingsTab = lazy(() => import('../components/admin/LiveSessionRecordingsTab'))
const FeatureAnnouncementsTab = lazy(() => import('../components/admin/FeatureAnnouncementsTab'))
const ConsentWaiversTab = lazy(() => import('../components/admin/ConsentWaiversTab'))
const MarketingUsersTab = lazy(() => import('../components/admin/MarketingUsersTab'))
const MarketingDashboardContent = lazy(() =>
  import('./MarketingDashboard').then((m) => ({ default: m.MarketingDashboardContent }))
)
const UserTagsManager = lazy(() => import('../components/admin/users/UserTagsManager'))
const TutorGroupsTab = lazy(() => import('../components/admin/TutorGroupsTab'))

// Shared loading fallback for lazy admin tabs (matches the app's <Suspense> spinner UX).
const TabLoadingFallback = () => (
  <div className="flex items-center justify-center h-64">
    <LoadingSpinner size="lg" />
  </div>
)

// Persist the Organizations grid's filters/search to localStorage so the admin's
// view survives navigation (per CLAUDE.md admin-table convention).
const ORG_GRID_STORAGE_KEY = 'admin:organizations:grid'

const loadOrgGridConfig = () => {
  try {
    const raw = localStorage.getItem(ORG_GRID_STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

const AdminDashboard = () => {
  const { user: currentUser, isLoading: authLoading } = useAuthStore()
  const location = useLocation()
  const navigate = useNavigate()

  // Online users tracking via WebSocket
  const {
    onlineCount,
    users: onlineUsers,
    isLoading: onlineUsersLoading,
    isConnected: wsConnected
  } = useOnlineUsers()

  const validTabs = [
    'overview',
    'users',
    'organizations',
    'data',
    'invitations',
    'waitlist',
    'referrals',
    'feedback',
    'analytics',
    'logs',
    'audit-logs',
    'compliance',
    'vaults',
    'questions',
    'challenge-types',
    'knowledge-graph',
    'jobs',
    'email-management',
    'queue-monitor',
    'fomo',
    'ai-advisor',
    'database-query',
    'licensing',
    'feature-flags',
    'schools',
    'coach-applications',
    'campaigns',
    'feature-announcements',
    'user-tags',
    'events',
    'live-sessions',
    'consent-waivers',
    'tutor-groups',
    'marketing-users',
    'marketing',
    'whitelabeling'
  ]

  // Initialize activeTab from URL hash or default to 'overview'
  const getTabFromHash = () => {
    const hash = location.hash.replace('#', '')
    // Extract tab name before any query params or sub-tabs
    // e.g., "feedback?id=xxx" -> "feedback", "email-management/analytics" -> "email-management"
    const tabName = hash.split('?')[0].split('/')[0]
    return validTabs.includes(tabName) ? tabName : 'overview'
  }

  const [activeTab, setActiveTab] = useState(getTabFromHash())

  // Fill the viewport for each inline admin table (one renders at a time). Gating on activeTab makes
  // the hook re-measure when its tab becomes active, so the table fills reliably on tab switch.
  const usersTableRef = useRef(null)
  const usersTableFillHeight = useFillViewportHeight(usersTableRef, {
    enabled: activeTab === 'users'
  })
  const orgsTableRef = useRef(null)
  const orgsTableFillHeight = useFillViewportHeight(orgsTableRef, {
    enabled: activeTab === 'organizations'
  })
  const invitesTableRef = useRef(null)
  const invitesTableFillHeight = useFillViewportHeight(invitesTableRef, {
    enabled: activeTab === 'invitations'
  })

  // User Management State
  const [users, setUsers] = useState([])
  const [userLoading, setUserLoading] = useState(false)
  const [userActionLoading, setUserActionLoading] = useState(null)
  const [selectedUserId, setSelectedUserId] = useState(null)
  const [showUserDetailsModal, setShowUserDetailsModal] = useState(false)
  const [showOnlineUsersModal, setShowOnlineUsersModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [showSchoolImportModal, setShowSchoolImportModal] = useState(false)
  // Bumped when an import modal reports a finished job so the jobs panel refetches
  // right away rather than waiting for its next poll tick.
  const [jobsRefreshSignal, setJobsRefreshSignal] = useState(0)
  const [usersPagination, setUsersPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    hasMore: false
  })
  const [usersSearch, setUsersSearch] = useState('')
  const debouncedUsersSearch = useDebounce(usersSearch, 300) // 300ms debounce delay
  const [usersRoleFilter, setUsersRoleFilter] = useState('')
  const [usersStatusFilter, setUsersStatusFilter] = useState('')

  // Disable/Enable dialog state
  const [showDisableDialog, setShowDisableDialog] = useState(false)
  const [disableReason, setDisableReason] = useState('')
  const [userToDisable, setUserToDisable] = useState(null)
  const [showEnableDialog, setShowEnableDialog] = useState(false)
  const [userToEnable, setUserToEnable] = useState(null)

  // Clone user modal state
  const [userToClone, setUserToClone] = useState(null)

  // Organizations Management State
  const [organizations, setOrganizations] = useState([])
  const [organizationsLoading, setOrganizationsLoading] = useState(false)
  const [organizationsPagination, setOrganizationsPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  })
  // Read the persisted grid config once at mount (not per render, not twice).
  const savedOrgGridConfig = useMemo(() => loadOrgGridConfig(), [])
  const [organizationsSearch, setOrganizationsSearch] = useState(savedOrgGridConfig.search || '')
  const [organizationsTypeFilter, setOrganizationsTypeFilter] = useState(
    savedOrgGridConfig.type || ''
  )
  const [organizationTypes, setOrganizationTypes] = useState([])
  const [selectedOrganization, setSelectedOrganization] = useState(null)
  const [organizationDetailsLoading, setOrganizationDetailsLoading] = useState(false)
  const [showOrganizationModal, setShowOrganizationModal] = useState(false)
  // Create / edit organization modal. orgFormTarget === null while open means "create".
  const [showOrgFormModal, setShowOrgFormModal] = useState(false)
  const [orgFormTarget, setOrgFormTarget] = useState(null)

  // Challenge Metrics State
  const [challengeMetrics, setChallengeMetrics] = useState(null)
  const [metricsLoading, setMetricsLoading] = useState(false)

  // Invitations State
  const [invitations, setInvitations] = useState([])
  const [invitationStatusFilter, setInvitationStatusFilter] = useState('pending') // 'all' | 'pending' | 'accepted' | 'expired'
  const [invitationEmailSearch, setInvitationEmailSearch] = useState('')
  const [showInvitationForm, setShowInvitationForm] = useState(false)
  const [invitationForm, setInvitationForm] = useState({
    email: '',
    role: 'member',
    expirationHours: 168,
    startingLevel: 1,
    expiresAt: '',
    isAdmin: false
  })
  const [isCreatingInvitation, setIsCreatingInvitation] = useState(false)
  const [copiedInvitationId, setCopiedInvitationId] = useState(null)
  const [extendInvitationId, setExtendInvitationId] = useState(null)
  const [selectedExtendHours, setSelectedExtendHours] = useState(168) // Default 7 days
  const [isExtendingInvitation, setIsExtendingInvitation] = useState(false)
  const [deleteInvitationData, setDeleteInvitationData] = useState(null) // { id, status } for delete confirmation
  const [resendInvitationData, setResendInvitationData] = useState(null) // { id, email } for resend confirmation
  const [isResendingInvitation, setIsResendingInvitation] = useState(false)
  const [emailEventsModalData, setEmailEventsModalData] = useState(null) // { invitationId, email, events }
  const [isLoadingEmailEvents, setIsLoadingEmailEvents] = useState(false)
  const [selectedInvitationIds, setSelectedInvitationIds] = useState([])
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false)
  const [bulkResendLoading, setBulkResendLoading] = useState(false)
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false)
  const [bulkActivateLoading, setBulkActivateLoading] = useState(false)
  const [bulkArchiveLoading, setBulkArchiveLoading] = useState(false)
  const [bulkArchiveConfirm, setBulkArchiveConfirm] = useState(false)
  const [invitationRoleFilter, setInvitationRoleFilter] = useState('all')
  const [invitationDateFrom, setInvitationDateFrom] = useState('')
  const [invitationDateTo, setInvitationDateTo] = useState('')
  const [error, setError] = useState(null)

  // Sync activeTab with URL hash (using native hashchange for reliable detection)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '')
      // Extract tab name before any query params or sub-tabs
      // e.g., "feedback?id=xxx" -> "feedback", "email-management/analytics" -> "email-management"
      const tabName = hash.split('?')[0].split('/')[0]
      if (tabName && validTabs.includes(tabName)) {
        setActiveTab(tabName)
      }
    }

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange)

    // Also check on mount and when location changes
    handleHashChange()

    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [location.hash])

  // Update URL hash when activeTab changes
  const handleTabChange = (tabId) => {
    setActiveTab(tabId)
    navigate(`/admin#${tabId}`, { replace: true })
  }

  // Marketing-only users (not admins) reach Marketing at /admin#marketing and see ONLY the
  // marketing dashboard — never the rest of the admin surface.
  if (!currentUser?.isAdmin && currentUser?.isMarketing) {
    return (
      <Suspense fallback={<TabLoadingFallback />}>
        <MarketingDashboardContent />
      </Suspense>
    )
  }

  // Everyone else needs admin privileges.
  if (!currentUser?.isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 dark:text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Access Denied
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            You need admin privileges to access this page.
          </p>
        </div>
      </div>
    )
  }

  const loadUsers = async (page = 1) => {
    try {
      setUserLoading(true)
      const params = new URLSearchParams({
        page: page.toString(),
        limit: usersPagination.limit.toString()
      })

      if (debouncedUsersSearch) {
        params.append('search', debouncedUsersSearch)
      }

      if (usersRoleFilter) {
        params.append('role', usersRoleFilter)
      }

      if (usersStatusFilter) {
        params.append('status', usersStatusFilter)
      }

      const response = await fetch(`/api/admin/users?${params}`, {
        credentials: 'include' // Session-based auth with cookies
      })

      if (!response.ok) {
        throw new Error('Failed to load users')
      }

      const data = await response.json()
      setUsers(data.users)
      if (data.pagination) {
        setUsersPagination(data.pagination)
      }
    } catch (error) {
      logger.error('Error loading users:', error)
      toast.error('Failed to load users')
    } finally {
      setUserLoading(false)
    }
  }

  const loadOrganizations = async (page = 1) => {
    try {
      setOrganizationsLoading(true)
      const params = new URLSearchParams({
        page: page.toString(),
        limit: organizationsPagination.limit.toString()
      })

      if (organizationsSearch) {
        params.append('search', organizationsSearch)
      }

      if (organizationsTypeFilter) {
        params.append('type', organizationsTypeFilter)
      }

      const response = await fetch(`/api/admin/organizations?${params}`, {
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error('Failed to load organizations')
      }

      const data = await response.json()
      setOrganizations(data.organizations)
      setOrganizationsPagination(data.pagination)
    } catch (error) {
      logger.error('Error loading organizations:', error)
      toast.error('Failed to load organizations')
    } finally {
      setOrganizationsLoading(false)
    }
  }

  const loadOrganizationTypes = async () => {
    try {
      const response = await fetch('/api/admin/organizations/types', {
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error('Failed to load organization types')
      }

      const data = await response.json()
      setOrganizationTypes(data.types)
    } catch (error) {
      logger.error('Error loading organization types:', error)
    }
  }

  const loadOrganizationDetails = async (organizationId) => {
    try {
      setOrganizationDetailsLoading(true)
      setShowOrganizationModal(true)

      const response = await fetch(`/api/admin/organizations/${organizationId}`, {
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error('Failed to load organization details')
      }

      const data = await response.json()
      setSelectedOrganization(data.organization)
    } catch (error) {
      logger.error('Error loading organization details:', error)
      toast.error('Failed to load organization details')
      setShowOrganizationModal(false)
    } finally {
      setOrganizationDetailsLoading(false)
    }
  }

  const closeOrganizationModal = () => {
    setShowOrganizationModal(false)
    setSelectedOrganization(null)
  }

  const openCreateOrganization = () => {
    setOrgFormTarget(null)
    setShowOrgFormModal(true)
  }

  const openEditOrganization = (org) => {
    setOrgFormTarget(org)
    setShowOrgFormModal(true)
  }

  const handleSubmitOrganization = async (payload) => {
    try {
      if (orgFormTarget) {
        await organizationAdminService.adminUpdate(orgFormTarget.id, payload)
        toast.success('Organization updated')
        setShowOrgFormModal(false)
        setOrgFormTarget(null)
        await loadOrganizations(organizationsPagination.page)
        // Refresh the open details modal if it's showing this org
        if (selectedOrganization?.id === orgFormTarget.id) {
          loadOrganizationDetails(orgFormTarget.id)
        }
      } else {
        const result = await organizationAdminService.adminCreate(payload)
        toast.success('Organization created')
        setShowOrgFormModal(false)
        await loadOrganizations(1)
        loadOrganizationTypes()
        // Open the new org's details so the admin can add members right away
        if (result?.organization?.id) {
          loadOrganizationDetails(result.organization.id)
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save organization')
      throw error
    }
  }

  const toggleUserStatus = async (userId, isDisabled, reason = '') => {
    try {
      setUserActionLoading(userId)

      const response = await fetch(`/api/admin/users/${userId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include', // Session-based auth with cookies
        body: JSON.stringify({ isDisabled, reason })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to update user status')
      }

      const result = await response.json()
      toast.success(result.message)
      await loadUsers()
    } catch (error) {
      logger.error('Error updating user status:', error)
      toast.error(error.message)
    } finally {
      setUserActionLoading(null)
    }
  }

  const handleCreateInvitation = async (e) => {
    e.preventDefault()

    // Client-side validation with specific error messages
    const validationErrors = []

    if (!invitationForm.email.trim()) {
      validationErrors.push('Email address is required')
    }

    if (!invitationForm.startingLevel) {
      validationErrors.push('Please select a starting level')
    }

    if (validationErrors.length > 0) {
      validationErrors.forEach((error) => toast.error(error))
      return
    }

    setIsCreatingInvitation(true)

    try {
      const response = await fetch('/api/invitations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include', // Session-based auth with cookies
        body: JSON.stringify({
          email: invitationForm.email,
          role: invitationForm.role,
          expirationHours: invitationForm.expirationHours,
          grantAdminPrivileges: invitationForm.isAdmin
        })
      })

      const result = await response.json()

      if (response.ok) {
        toast.success('Invitation created successfully!')
        setInvitationForm({
          email: '',
          role: 'member',
          expirationHours: 168,
          startingLevel: 1,
          expiresAt: '',
          isAdmin: false
        })
        setShowInvitationForm(false)
        await loadInvitations() // Reload invitations
      } else {
        toast.error(getErrorMessage(result.error, 'Failed to create invitation'))
      }
    } catch (error) {
      logger.error('Error creating invitation:', error)
      toast.error('Failed to create invitation')
    } finally {
      setIsCreatingInvitation(false)
    }
  }

  const handleDeleteInvitation = async (invitationId) => {
    logger.debug('🗑️ Attempting to delete invitation:', invitationId)

    try {
      const url = `/api/invitations/${invitationId}`
      logger.debug('🌐 DELETE URL:', url)

      const response = await fetch(url, {
        method: 'DELETE',
        credentials: 'include' // Session-based auth with cookies
      })

      logger.debug('📊 Response status:', response.status)
      logger.debug('📊 Response ok:', response.ok)

      const result = await response.json()
      logger.debug('📊 Response data:', result)

      if (response.ok) {
        toast.success('Invitation deleted successfully!')
        await loadInvitations() // Reload invitations
      } else {
        if (result.error === 'Route not found') {
          toast.error(
            'Delete route not available. Please restart the server or use command line delete.'
          )
          logger.debug('💡 Server needs restart to enable DELETE route')
        } else {
          toast.error(getErrorMessage(result.error, 'Failed to delete invitation'))
        }
      }
    } catch (error) {
      logger.error('❌ Delete invitation error:', error)
      toast.error('Failed to delete invitation')
    }
  }

  const handleCopyInvitationLink = async (invitation) => {
    const link = `${window.location.origin}/register?token=${invitation.invitationToken}`
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(link)
      } else {
        const textArea = document.createElement('textarea')
        textArea.value = link
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
      }
      setCopiedInvitationId(invitation.id)
      toast.success('Invitation link copied!')
      setTimeout(() => setCopiedInvitationId(null), 2000)
    } catch (error) {
      logger.error('Failed to copy:', error)
      toast.error('Failed to copy link')
    }
  }

  const handleExtendInvitation = async () => {
    if (!extendInvitationId) return

    setIsExtendingInvitation(true)
    try {
      const response = await fetch(`/api/invitations/${extendInvitationId}/extend`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ hours: selectedExtendHours })
      })

      if (response.ok) {
        toast.success('Invitation extended successfully!')
        await loadInvitations()
        setExtendInvitationId(null)
        setSelectedExtendHours(168) // Reset to default
      } else {
        const result = await response.json()
        toast.error(result.message || 'Failed to extend invitation')
      }
    } catch (error) {
      logger.error('Failed to extend invitation:', error)
      toast.error('Failed to extend invitation')
    } finally {
      setIsExtendingInvitation(false)
    }
  }

  const handleResendInvitation = async () => {
    if (!resendInvitationData) return

    setIsResendingInvitation(true)
    try {
      const response = await fetch(`/api/invitations/${resendInvitationData.id}/resend`, {
        method: 'POST',
        credentials: 'include'
      })

      if (response.ok) {
        toast.success('Invitation email resent successfully!')
        setResendInvitationData(null)
        // Refresh invitations to show updated email count
        await loadInvitations()
      } else {
        const result = await response.json()
        toast.error(result.message || 'Failed to resend invitation')
      }
    } catch (error) {
      logger.error('Failed to resend invitation:', error)
      toast.error('Failed to resend invitation')
    } finally {
      setIsResendingInvitation(false)
    }
  }

  const handleViewEmailEvents = async (invitation) => {
    setIsLoadingEmailEvents(true)
    try {
      const response = await fetch(`/api/invitations/${invitation.id}/email-events`, {
        credentials: 'include'
      })

      if (response.ok) {
        const result = await response.json()
        setEmailEventsModalData(result.data)
      } else {
        toast.error('Failed to load email history')
      }
    } catch (error) {
      logger.error('Failed to load email events:', error)
      toast.error('Failed to load email history')
    } finally {
      setIsLoadingEmailEvents(false)
    }
  }

  // Invitation filtering (memoized to avoid re-filtering on every render)
  const filteredInvitations = useMemo(() => {
    return invitations.filter((inv) => {
      if (invitationStatusFilter !== 'all' && inv.displayStatus !== invitationStatusFilter)
        return false
      if (
        invitationEmailSearch &&
        !inv.email.toLowerCase().includes(invitationEmailSearch.toLowerCase())
      )
        return false
      if (invitationRoleFilter !== 'all' && (inv.role || '').toLowerCase() !== invitationRoleFilter)
        return false
      if (invitationDateFrom) {
        if (new Date(inv.createdAt) < new Date(invitationDateFrom)) return false
      }
      if (invitationDateTo) {
        const to = new Date(invitationDateTo)
        to.setHours(23, 59, 59, 999)
        if (new Date(inv.createdAt) > to) return false
      }
      return true
    })
  }, [
    invitations,
    invitationStatusFilter,
    invitationEmailSearch,
    invitationRoleFilter,
    invitationDateFrom,
    invitationDateTo
  ])

  // Invitation selection handlers
  const toggleInvitationSelect = (id) => {
    setSelectedInvitationIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )
  }

  const toggleInvitationSelectAll = () => {
    const selectableIds = filteredInvitations.map((inv) => inv.id)
    if (selectedInvitationIds.length === selectableIds.length && selectableIds.length > 0) {
      setSelectedInvitationIds([])
    } else {
      setSelectedInvitationIds(selectableIds)
    }
  }

  const handleBulkDeleteInvitations = async () => {
    if (selectedInvitationIds.length === 0) return
    setBulkDeleteLoading(true)
    try {
      const response = await fetch('/api/invitations/bulk-delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ids: selectedInvitationIds })
      })
      const data = await response.json()
      if (response.ok && data.success) {
        toast.success(data.message)
        if (data.data?.failed?.length > 0) {
          toast.error(`${data.data.failed.length} failed to delete`)
        }
        setSelectedInvitationIds([])
        setBulkDeleteConfirm(false)
        await loadInvitations()
      } else {
        toast.error(data.message || 'Failed to delete invitations')
      }
    } catch (error) {
      logger.error('Error bulk deleting:', error)
      toast.error('Failed to delete invitations')
    } finally {
      setBulkDeleteLoading(false)
    }
  }

  const handleBulkResendInvitations = async () => {
    if (selectedInvitationIds.length === 0) return
    setBulkResendLoading(true)
    try {
      const response = await fetch('/api/invitations/bulk-resend', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ids: selectedInvitationIds })
      })
      const data = await response.json()
      if (response.ok && data.success) {
        toast.success(data.message)
        if (data.data?.failed?.length > 0) {
          toast.error(`${data.data.failed.length} failed to send`)
        }
        if (data.data?.skipped?.length > 0) {
          toast(`${data.data.skipped.length} skipped (not active)`, { icon: '⏭️' })
        }
        setSelectedInvitationIds([])
        await loadInvitations()
      } else {
        toast.error(data.message || 'Failed to resend invitations')
      }
    } catch (error) {
      logger.error('Error bulk resending:', error)
      toast.error('Failed to resend invitations')
    } finally {
      setBulkResendLoading(false)
    }
  }

  const handleBulkActivateInvitations = async () => {
    if (selectedInvitationIds.length === 0) return
    setBulkActivateLoading(true)
    try {
      const { data } = await api.post('/invitations/bulk-activate', { ids: selectedInvitationIds })
      toast.success(data.message)
      if (data.data?.failed?.length > 0) {
        toast.error(`${data.data.failed.length} failed to activate`)
      }
      if (data.data?.skipped?.length > 0) {
        toast(`${data.data.skipped.length} skipped (already active or accepted)`)
      }
      setSelectedInvitationIds([])
      await loadInvitations()
    } catch (error) {
      logger.error('Error bulk activating:', error)
      toast.error('Failed to activate invitations')
    } finally {
      setBulkActivateLoading(false)
    }
  }

  const handleBulkArchiveInvitations = async () => {
    if (selectedInvitationIds.length === 0) return
    setBulkArchiveLoading(true)
    try {
      const { data } = await api.post('/invitations/bulk-archive', { ids: selectedInvitationIds })
      toast.success(data.message)
      if (data.data?.failed?.length > 0) {
        toast.error(`${data.data.failed.length} failed to archive`)
      }
      if (data.data?.skipped?.length > 0) {
        toast(`${data.data.skipped.length} skipped (accepted or already archived)`)
      }
      setSelectedInvitationIds([])
      setBulkArchiveConfirm(false)
      await loadInvitations()
    } catch (error) {
      logger.error('Error bulk archiving:', error)
      toast.error('Failed to archive invitations')
    } finally {
      setBulkArchiveLoading(false)
    }
  }

  const loadInvitations = async () => {
    try {
      const authStorage = localStorage.getItem('auth-storage')
      // Session-based auth - no token needed, cookie is sent automatically
      logger.debug('📧 Loading all invitations...')
      const invitationsResponse = await fetch('/api/invitations', {
        credentials: 'include' // Ensure cookies are sent
      })

      if (invitationsResponse.ok) {
        const invitationsData = await invitationsResponse.json()
        logger.debug('✅ Invitations loaded:', invitationsData.data?.length || 0)
        logger.debug('📧 Invitations data:', invitationsData)
        setInvitations(invitationsData.data || [])
      } else {
        const errorData = await invitationsResponse.json().catch(() => ({}))
        logger.error(
          '❌ Failed to load user squad invitations:',
          invitationsResponse.status,
          errorData
        )
        setError(`Failed to load invitations: ${errorData.error || invitationsResponse.statusText}`)
      }
    } catch (error) {
      logger.error('❌ Error loading invitations:', error)
      setError(`Error loading invitations: ${error.message}`)
    }
  }

  const loadChallengeMetrics = async () => {
    try {
      setMetricsLoading(true)
      const response = await fetch('/api/admin/challenge-metrics', {
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error('Failed to load challenge metrics')
      }

      const data = await response.json()
      setChallengeMetrics(data.data)
    } catch (error) {
      logger.error('Error loading challenge metrics:', error)
      toast.error('Failed to load challenge metrics')
    } finally {
      setMetricsLoading(false)
    }
  }

  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers()
    } else if (activeTab === 'organizations') {
      loadOrganizations()
      loadOrganizationTypes()
    }
  }, [activeTab])

  // Reload organizations when search or filter changes
  useEffect(() => {
    if (activeTab === 'organizations') {
      loadOrganizations(1)
    }
  }, [organizationsSearch, organizationsTypeFilter])

  // Persist Organizations grid config (search + type filter) to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(
        ORG_GRID_STORAGE_KEY,
        JSON.stringify({ search: organizationsSearch, type: organizationsTypeFilter })
      )
    } catch {
      // ignore storage failures (e.g. private mode)
    }
  }, [organizationsSearch, organizationsTypeFilter])

  // Reload users when search or filters change
  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers(1)
    }
  }, [debouncedUsersSearch, usersRoleFilter, usersStatusFilter])

  // Load initial data on component mount when auth is complete
  useEffect(() => {
    if (!authLoading && currentUser) {
      loadInvitations()
      loadUsers()
      loadChallengeMetrics()
    }
  }, [authLoading, currentUser])

  const formatDate = (dateString) => {
    if (!dateString) return 'Never'
    return new Date(dateString).toLocaleString()
  }

  const getOrgTypeColor = (type) => {
    const typeColors = {
      family: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
      school: 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200',
      business: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200',
      nonprofit: 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200',
      government: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
    }
    return (
      typeColors[type?.toLowerCase()] ||
      'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
    )
  }

  const getStatusBadge = (user) => {
    if (user.isDisabled) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 dark:text-red-300">
          <UserX className="w-3 h-3 mr-1" />
          Disabled
        </span>
      )
    }

    if (user.isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
          <UserCheck className="w-3 h-3 mr-1" />
          Active
        </span>
      )
    }

    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">
        <AlertTriangle className="w-3 h-3 mr-1" />
        Inactive
      </span>
    )
  }

  const getRoleBadge = (user) => {
    if (user.isAdmin) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
          <Shield className="w-3 h-3 mr-1" />
          Admin
        </span>
      )
    }

    if (user.accountType === 'guardian') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
          <UserCheck className="w-3 h-3 mr-1" />
          Guardian
        </span>
      )
    }

    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
        <GraduationCap className="w-3 h-3 mr-1" />
        Student
      </span>
    )
  }

  const getInvitationRoleBadge = (role) => {
    const roleLower = (role || '').toLowerCase()
    if (roleLower === 'admin') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
          <Shield className="w-3 h-3 mr-1" />
          Admin
        </span>
      )
    }
    if (roleLower === 'guardian') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
          <UserCheck className="w-3 h-3 mr-1" />
          Guardian
        </span>
      )
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
        <GraduationCap className="w-3 h-3 mr-1" />
        Student
      </span>
    )
  }

  const getRelativeTime = (date) => {
    const now = new Date()
    const target = new Date(date)
    const diffMs = target - now
    const isPast = diffMs < 0
    const absDiff = Math.abs(diffMs)

    const minutes = Math.floor(absDiff / (1000 * 60))
    const hours = Math.floor(absDiff / (1000 * 60 * 60))
    const days = Math.floor(absDiff / (1000 * 60 * 60 * 24))

    if (isPast) {
      if (days > 0) return `${days}d ago`
      if (hours > 0) return `${hours}h ago`
      return `${minutes}m ago`
    }

    if (days > 0) return `${days}d`
    if (hours > 0) return `${hours}h`
    return `${minutes}m`
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: Settings },
    { id: 'users', name: 'User Management', icon: Users },
    { id: 'organizations', name: 'Organizations', icon: Building2 },
    { id: 'data', name: 'Data Management', icon: Database },
    { id: 'invitations', name: 'Invitations', icon: Mail },
    { id: 'waitlist', name: 'Waitlist', icon: ClipboardList },
    { id: 'referrals', name: 'Referrals', icon: Gift },
    { id: 'campaigns', name: 'Campaigns', icon: Target },
    { id: 'feature-announcements', name: 'Feature Announcements', icon: Sparkles },
    { id: 'ai-advisor', name: 'AI Advisor', icon: Sparkles },
    { id: 'consent-waivers', name: 'Tutoring Waivers', icon: ShieldCheck },
    { id: 'tutor-groups', name: 'Tutoring Pods', icon: Users },
    { id: 'feedback', name: 'Feedback', icon: MessageSquare },
    { id: 'audit-logs', name: 'Audit Logs', icon: ShieldCheck },
    { id: 'compliance', name: 'Compliance', icon: Shield },
    { id: 'vaults', name: 'Vaults', icon: Database },
    { id: 'questions', name: 'Questions', icon: FileQuestion },
    { id: 'challenge-types', name: 'Challenge Types', icon: Gamepad2 },
    { id: 'knowledge-graph', name: 'Knowledge Graph', icon: Network },
    { id: 'email-management', name: 'Email Management', icon: Send },
    { id: 'queue-monitor', name: 'Queue Monitor', icon: Activity },
    { id: 'licensing', name: 'Licensing', icon: CreditCard },
    { id: 'feature-flags', name: 'Feature Flags', icon: Flag },
    { id: 'schools', name: 'Schools', icon: School },
    { id: 'coach-applications', name: 'Coach Applications', icon: GraduationCap },
    { id: 'user-tags', name: 'User Tags', icon: Tag },
    { id: 'events', name: 'Events', icon: Calendar },
    { id: 'live-sessions', name: 'Live Sessions', icon: Video },
    { id: 'marketing-users', name: 'Marketing Users', icon: Megaphone },
    { id: 'marketing', name: 'Marketing', icon: Film },
    { id: 'whitelabeling', name: 'Whitelabeling', icon: Palette }
  ]

  // Show loading while authentication is in progress
  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div
        className={
          activeTab === 'database-query'
            ? 'py-6 px-4 sm:px-6 lg:px-8'
            : 'mx-auto py-6 px-4 sm:px-6 lg:px-8'
        }
      >
        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Title */}
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Overview</h2>
            </div>

            {/* User Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
                <div className="flex items-center">
                  <Users className="w-6 h-6 sm:w-8 sm:h-8 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                  <div className="ml-3 sm:ml-4 min-w-0">
                    <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 truncate">
                      Total Users
                    </p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {usersPagination.total}
                    </p>
                  </div>
                </div>
              </div>

              {/* Online Users - Real-time via WebSocket */}
              <div
                className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6 relative overflow-hidden cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750 hover:shadow-md transition-all"
                onClick={() => setShowOnlineUsersModal(true)}
                title="Click to view online users"
              >
                <div className="flex items-center">
                  <div className="relative">
                    <Wifi className="w-6 h-6 sm:w-8 sm:h-8 text-green-600 dark:text-green-400 flex-shrink-0" />
                    {wsConnected && (
                      <span className="absolute -top-1 -right-1 flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                      </span>
                    )}
                  </div>
                  <div className="ml-3 sm:ml-4 min-w-0">
                    <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 truncate flex items-center gap-1.5">
                      Online Now
                      {wsConnected && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          Live
                        </span>
                      )}
                    </p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {onlineUsersLoading ? '...' : onlineCount}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
                <div className="flex items-center">
                  <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                  <div className="ml-3 sm:ml-4 min-w-0">
                    <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 truncate">
                      Admins
                    </p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {users.filter((u) => u.isAdmin).length}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
                <div className="flex items-center">
                  <Mail className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                  <div className="ml-3 sm:ml-4 min-w-0">
                    <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 truncate">
                      Invitations
                    </p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {invitations.length}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
                <div className="flex items-center">
                  <Target className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                  <div className="ml-3 sm:ml-4 min-w-0">
                    <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 truncate">
                      Total Challenges
                    </p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {metricsLoading ? '...' : (challengeMetrics?.activity?.total ?? 0)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Challenge Activity Cards */}
            <div>
              <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                Challenge Activity
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
                  <div className="flex items-center">
                    <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-600 dark:text-green-400 flex-shrink-0" />
                    <div className="ml-3 sm:ml-4 min-w-0">
                      <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 truncate">
                        Completed
                      </p>
                      <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {metricsLoading
                          ? '...'
                          : (challengeMetrics?.activity?.byStatus?.completed ?? 0)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
                  <div className="flex items-center">
                    <Activity className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
                    <div className="ml-3 sm:ml-4 min-w-0">
                      <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 truncate">
                        Active
                      </p>
                      <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {metricsLoading
                          ? '...'
                          : (challengeMetrics?.activity?.byStatus?.active ?? 0)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
                  <div className="flex items-center">
                    <FileQuestion className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                    <div className="ml-3 sm:ml-4 min-w-0">
                      <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 truncate">
                        Questions Answered
                      </p>
                      <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {metricsLoading
                          ? '...'
                          : (challengeMetrics?.activity?.questionsAnswered ?? 0).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
                  <div className="flex items-center">
                    <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                    <div className="ml-3 sm:ml-4 min-w-0">
                      <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 truncate">
                        Completion Rate
                      </p>
                      <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {metricsLoading
                          ? '...'
                          : `${challengeMetrics?.performance?.averageCompletionRate ?? 0}%`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Performance Cards */}
            <div>
              <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                Performance Metrics
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
                  <div className="flex items-center">
                    <Award className="w-6 h-6 sm:w-8 sm:h-8 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                    <div className="ml-3 sm:ml-4 min-w-0">
                      <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 truncate">
                        Average Score
                      </p>
                      <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {metricsLoading
                          ? '...'
                          : `${Math.round(challengeMetrics?.performance?.averageScore ?? 0)}%`}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
                  <div className="flex items-center">
                    <Target className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                    <div className="ml-3 sm:ml-4 min-w-0">
                      <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 truncate">
                        Accuracy Rate
                      </p>
                      <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {metricsLoading
                          ? '...'
                          : `${challengeMetrics?.performance?.averageAccuracy ?? 0}%`}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
                  <div className="flex items-center">
                    <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-slate-600 dark:text-slate-400 flex-shrink-0" />
                    <div className="ml-3 sm:ml-4 min-w-0">
                      <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 truncate">
                        Avg Time
                      </p>
                      <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {metricsLoading
                          ? '...'
                          : `${Math.round((challengeMetrics?.performance?.averageTimeSeconds ?? 0) / 60)} min`}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
                  <div className="flex items-center">
                    <Gamepad2 className="w-6 h-6 sm:w-8 sm:h-8 text-pink-600 dark:text-pink-400 flex-shrink-0" />
                    <div className="ml-3 sm:ml-4 min-w-0">
                      <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 truncate">
                        Draft Challenges
                      </p>
                      <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {metricsLoading
                          ? '...'
                          : (challengeMetrics?.activity?.byStatus?.draft ?? 0)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Time Period Comparison */}
            <div>
              <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                Recent Activity
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Last 24 Hours
                    </h4>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Daily</span>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {metricsLoading
                          ? '...'
                          : (challengeMetrics?.activity?.last24Hours?.total ?? 0)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Challenges</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {metricsLoading
                          ? '...'
                          : (challengeMetrics?.activity?.last24Hours?.completed ?? 0)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Completed</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                        {metricsLoading
                          ? '...'
                          : `${Math.round(challengeMetrics?.performance?.last24Hours?.averageScore ?? 0)}%`}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Avg Score</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Last 7 Days
                    </h4>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Weekly</span>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {metricsLoading
                          ? '...'
                          : (challengeMetrics?.activity?.last7Days?.total ?? 0)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Challenges</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {metricsLoading
                          ? '...'
                          : (challengeMetrics?.activity?.last7Days?.completed ?? 0)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Completed</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                        {metricsLoading
                          ? '...'
                          : `${Math.round(challengeMetrics?.performance?.last7Days?.averageScore ?? 0)}%`}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Avg Score</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Last 30 Days
                    </h4>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Monthly</span>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {metricsLoading
                          ? '...'
                          : (challengeMetrics?.activity?.last30Days?.total ?? 0)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Challenges</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {metricsLoading
                          ? '...'
                          : (challengeMetrics?.activity?.last30Days?.completed ?? 0)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Completed</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                        {metricsLoading
                          ? '...'
                          : `${Math.round(challengeMetrics?.performance?.last30Days?.averageScore ?? 0)}%`}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Avg Score</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Top Users Leaderboard */}
            {challengeMetrics?.topUsers?.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
                <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Trophy className="w-5 h-5 text-yellow-500 mr-2" />
                      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                        Top Performers
                      </h3>
                    </div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Based on completed challenges
                    </span>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Rank
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Completed
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Avg Score
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Accuracy
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Time Spent
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {challengeMetrics.topUsers.map((user, index) => (
                        <tr
                          key={user.userId}
                          className={index < 3 ? 'bg-yellow-50 dark:bg-yellow-900/10' : ''}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {index === 0 && <span className="text-xl">&#129351;</span>}
                              {index === 1 && <span className="text-xl">&#129352;</span>}
                              {index === 2 && <span className="text-xl">&#129353;</span>}
                              {index > 2 && (
                                <span className="text-gray-500 dark:text-gray-400 font-medium">
                                  {user.rank}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {user.displayName}
                            </div>
                            {user.realName && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {user.realName}
                              </div>
                            )}
                            {user.email && (
                              <div className="text-xs text-gray-400 dark:text-gray-500">
                                {user.email}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-gray-100">
                              {user.challengesCompleted}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-gray-100">
                              {user.averageScore}%
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-gray-100">
                              {user.accuracy}%
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {Math.round(user.totalTimeSeconds / 60)} min
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                User Management
              </h2>
              <button
                onClick={() => loadUsers(usersPagination.page)}
                disabled={userLoading}
                className="btn btn-secondary"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${userLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {/* Search and Filters */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Search Users
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      value={usersSearch}
                      onChange={(e) => setUsersSearch(e.target.value)}
                      placeholder="Search by ID, name, public name, or email..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Role
                  </label>
                  <select
                    value={usersRoleFilter}
                    onChange={(e) => setUsersRoleFilter(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All Roles</option>
                    <option value="admin">Admins</option>
                    <option value="user">Users</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Status
                  </label>
                  <select
                    value={usersStatusFilter}
                    onChange={(e) => setUsersStatusFilter(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All Status</option>
                    <option value="active">Active</option>
                    <option value="disabled">Disabled</option>
                  </select>
                </div>
              </div>
            </div>

            {userLoading ? (
              <div className="flex items-center justify-center h-64">
                <LoadingSpinner size="lg" />
              </div>
            ) : (
              <div
                ref={usersTableRef}
                className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden flex flex-col"
                style={usersTableFillHeight ? { height: usersTableFillHeight } : undefined}
              >
                <div className="overflow-auto flex-1 min-h-0">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Role
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Orgs
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Last Login
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {users.map((user) => {
                        const displayName =
                          user.firstName && user.lastName
                            ? `${user.firstName} ${user.lastName}`
                            : user.email
                        const initials =
                          user.firstName && user.lastName
                            ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`
                            : user.email.substring(0, 2).toUpperCase()

                        return (
                          <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10">
                                  <div className="h-10 w-10 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                      {initials}
                                    </span>
                                  </div>
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {displayName}
                                  </div>
                                  {user.publicName && (
                                    <div className="text-xs text-indigo-500 dark:text-indigo-400">
                                      {user.publicName}
                                    </div>
                                  )}
                                  {user.firstName && user.lastName && (
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                      {user.email}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">{getRoleBadge(user)}</td>

                            <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(user)}</td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                <Building2 className="w-4 h-4 mr-1" />
                                {user.orgCount || 0}
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              <div className="flex items-center">
                                <Clock className="w-4 h-4 mr-1" />
                                {formatDate(user.lastLoginAt)}
                              </div>
                              <div className="text-xs text-gray-400 dark:text-gray-500">
                                {user.loginCount || 0} logins
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex space-x-2">
                                {/* View Details Button */}
                                <button
                                  onClick={() => {
                                    setSelectedUserId(user.id)
                                    setShowUserDetailsModal(true)
                                  }}
                                  className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                                  title="View details"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                {/* Clone User Button - super admins only. No extra guard
                                    needed: this page already renders nothing unless
                                    currentUser.isAdmin, and User.isAdmin IS the super-admin
                                    flag (exactly what requireSuperAdmin checks server-side;
                                    org admins are a separate membership role). */}
                                <button
                                  onClick={() => setUserToClone(user)}
                                  className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                                  title="Clone user"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                                {/* Enable/Disable Button - show for all users except self */}
                                {user.id !== currentUser?.id && (
                                  <>
                                    {user.isDisabled ? (
                                      <button
                                        onClick={() => {
                                          setUserToEnable(user)
                                          setShowEnableDialog(true)
                                        }}
                                        disabled={userActionLoading === user.id}
                                        className="text-green-600 hover:text-green-900 disabled:opacity-50"
                                        title="Enable user"
                                      >
                                        <UserCheck className="w-4 h-4" />
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => {
                                          setUserToDisable(user)
                                          setShowDisableDialog(true)
                                        }}
                                        disabled={userActionLoading === user.id}
                                        className="text-red-600 hover:text-red-900 disabled:opacity-50"
                                        title="Disable user"
                                      >
                                        <UserX className="w-4 h-4" />
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {usersPagination.totalPages > 1 && (
                  <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-600 sm:px-6 shrink-0">
                    <div className="flex-1 flex justify-between sm:hidden">
                      <button
                        onClick={() => loadUsers(usersPagination.page - 1)}
                        disabled={!usersPagination.hasPrevPage}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => loadUsers(usersPagination.page + 1)}
                        disabled={!usersPagination.hasNextPage}
                        className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          Showing{' '}
                          <span className="font-medium">
                            {(usersPagination.page - 1) * usersPagination.limit + 1}
                          </span>{' '}
                          to{' '}
                          <span className="font-medium">
                            {Math.min(
                              usersPagination.page * usersPagination.limit,
                              usersPagination.total
                            )}
                          </span>{' '}
                          of <span className="font-medium">{usersPagination.total}</span> users
                        </p>
                      </div>
                      <div>
                        <nav
                          className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                          aria-label="Pagination"
                        >
                          <button
                            onClick={() => loadUsers(usersPagination.page - 1)}
                            disabled={!usersPagination.hasPrevPage}
                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <ChevronLeft className="w-5 h-5" />
                            <span className="ml-1">Previous</span>
                          </button>
                          <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-300">
                            Page {usersPagination.page} of {usersPagination.totalPages}
                          </span>
                          <button
                            onClick={() => loadUsers(usersPagination.page + 1)}
                            disabled={!usersPagination.hasNextPage}
                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <span className="mr-1">Next</span>
                            <ChevronRight className="w-5 h-5" />
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'organizations' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Organizations</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => loadOrganizations(organizationsPagination.page)}
                  disabled={organizationsLoading}
                  className="btn btn-secondary"
                >
                  <RefreshCw
                    className={`w-4 h-4 mr-2 ${organizationsLoading ? 'animate-spin' : ''}`}
                  />
                  Refresh
                </button>
                <button onClick={openCreateOrganization} className="btn btn-primary">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Create Organization
                </button>
              </div>
            </div>

            {/* Search and Filter */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Search Organizations
                  </label>
                  <input
                    type="text"
                    value={organizationsSearch}
                    onChange={(e) => setOrganizationsSearch(e.target.value)}
                    placeholder="Search by name or slug..."
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Filter by Type
                  </label>
                  <select
                    value={organizationsTypeFilter}
                    onChange={(e) => setOrganizationsTypeFilter(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All Types</option>
                    {organizationTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {organizationsLoading ? (
              <div className="flex items-center justify-center h-64">
                <LoadingSpinner size="lg" />
              </div>
            ) : organizations.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-8 text-center">
                <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">No organizations found</p>
              </div>
            ) : (
              <div
                ref={orgsTableRef}
                className="flex flex-col gap-4"
                style={orgsTableFillHeight ? { height: orgsTableFillHeight } : undefined}
              >
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden flex-1 min-h-0 flex flex-col">
                  <div className="overflow-auto flex-1 min-h-0">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Organization
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Members
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Created By
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Created At
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {organizations.map((org) => {
                          const creatorName =
                            org.createdBy?.firstName && org.createdBy?.lastName
                              ? `${org.createdBy.firstName} ${org.createdBy.lastName}`
                              : org.createdBy?.email || 'Unknown'

                          return (
                            <tr key={org.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0 h-10 w-10">
                                    {org.logo ? (
                                      <img
                                        src={org.logo}
                                        alt={org.name}
                                        className="h-10 w-10 rounded-lg object-cover"
                                      />
                                    ) : (
                                      <div className="h-10 w-10 rounded-lg bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                                        <Building2 className="w-5 h-5 text-primary-600 dark:text-primary-300" />
                                      </div>
                                    )}
                                  </div>
                                  <div className="ml-4">
                                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                      {org.name}
                                    </div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                      {org.slug}
                                    </div>
                                  </div>
                                </div>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap">
                                <span
                                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getOrgTypeColor(org.type)}`}
                                >
                                  {org.type}
                                </span>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center text-sm text-gray-900 dark:text-gray-100">
                                  <Users className="w-4 h-4 mr-2 text-gray-400" />
                                  {org.memberCount}
                                </div>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {creatorName}
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                <div className="flex items-center">
                                  <Calendar className="w-4 h-4 mr-1" />
                                  {formatDate(org.createdAt)}
                                </div>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <button
                                  onClick={() => loadOrganizationDetails(org.id)}
                                  className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300 flex items-center"
                                  title="View Details"
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  Details
                                </button>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pagination */}
                {organizationsPagination.totalPages > 1 && (
                  <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6 rounded-lg shadow shrink-0">
                    <div className="flex-1 flex justify-between sm:hidden">
                      <button
                        onClick={() => loadOrganizations(organizationsPagination.page - 1)}
                        disabled={!organizationsPagination.hasPrevPage}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => loadOrganizations(organizationsPagination.page + 1)}
                        disabled={!organizationsPagination.hasNextPage}
                        className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          Showing{' '}
                          <span className="font-medium">
                            {(organizationsPagination.page - 1) * organizationsPagination.limit + 1}
                          </span>{' '}
                          to{' '}
                          <span className="font-medium">
                            {Math.min(
                              organizationsPagination.page * organizationsPagination.limit,
                              organizationsPagination.total
                            )}
                          </span>{' '}
                          of <span className="font-medium">{organizationsPagination.total}</span>{' '}
                          results
                        </p>
                      </div>
                      <div>
                        <nav
                          className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                          aria-label="Pagination"
                        >
                          <button
                            onClick={() => loadOrganizations(organizationsPagination.page - 1)}
                            disabled={!organizationsPagination.hasPrevPage}
                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Previous
                          </button>
                          <button
                            onClick={() => loadOrganizations(organizationsPagination.page + 1)}
                            disabled={!organizationsPagination.hasNextPage}
                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Organization Details Modal */}
            {showOrganizationModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                  {/* Modal Header */}
                  <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between items-center">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                      Organization Details
                    </h3>
                    <button
                      onClick={closeOrganizationModal}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>

                  {/* Modal Content */}
                  <div className="px-6 py-4">
                    {organizationDetailsLoading ? (
                      <div className="flex items-center justify-center h-64">
                        <LoadingSpinner size="lg" />
                      </div>
                    ) : selectedOrganization ? (
                      <div className="space-y-6">
                        {/* Organization Info */}
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                          <div className="flex items-start space-x-4">
                            <div className="flex-shrink-0">
                              {selectedOrganization.logo ? (
                                <img
                                  src={selectedOrganization.logo}
                                  alt={selectedOrganization.name}
                                  className="w-16 h-16 rounded-lg object-cover"
                                />
                              ) : (
                                <div className="w-16 h-16 rounded-lg bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                                  <Building2 className="w-8 h-8 text-primary-600 dark:text-primary-300" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-start justify-between gap-2">
                                <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                  {selectedOrganization.name}
                                </h4>
                                <button
                                  onClick={() => openEditOrganization(selectedOrganization)}
                                  className="flex-shrink-0 px-3 py-1 text-xs font-medium bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
                                >
                                  Edit details
                                </button>
                              </div>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {selectedOrganization.slug}
                              </p>
                              <div className="mt-2 flex items-center space-x-4 text-sm">
                                <span
                                  className={`px-2 py-1 rounded-full ${getOrgTypeColor(selectedOrganization.type)}`}
                                >
                                  {selectedOrganization.type}
                                </span>
                                {selectedOrganization.domain && (
                                  <span className="text-gray-600 dark:text-gray-400">
                                    {selectedOrganization.domain}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500 dark:text-gray-400">Created By:</span>
                              <p className="font-medium text-gray-900 dark:text-gray-100">
                                {selectedOrganization.createdBy?.firstName &&
                                selectedOrganization.createdBy?.lastName
                                  ? `${selectedOrganization.createdBy.firstName} ${selectedOrganization.createdBy.lastName}`
                                  : selectedOrganization.createdBy?.email || 'Unknown'}
                              </p>
                            </div>
                            <div>
                              <span className="text-gray-500 dark:text-gray-400">Created:</span>
                              <p className="font-medium text-gray-900 dark:text-gray-100">
                                {formatDate(selectedOrganization.createdAt)}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Statistics */}
                        {selectedOrganization.stats && (
                          <div className="grid grid-cols-3 gap-4">
                            <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Total Members
                                  </p>
                                  <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                                    {selectedOrganization.stats.totalMembers}
                                  </p>
                                </div>
                                <Users className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                              </div>
                            </div>
                            <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Pending Invites
                                  </p>
                                  <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                                    {selectedOrganization.stats.pendingInvitations}
                                  </p>
                                </div>
                                <Mail className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
                              </div>
                            </div>
                            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-sm text-gray-600 dark:text-gray-400">Roles</p>
                                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                                    {
                                      Object.keys(selectedOrganization.stats.roleDistribution || {})
                                        .length
                                    }
                                  </p>
                                </div>
                                <Shield className="w-8 h-8 text-green-600 dark:text-green-400" />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Role Distribution */}
                        {selectedOrganization.stats?.roleDistribution &&
                          Object.keys(selectedOrganization.stats.roleDistribution).length > 0 && (
                            <div>
                              <h5 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                                Role Distribution
                              </h5>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {Object.entries(selectedOrganization.stats.roleDistribution).map(
                                  ([role, count]) => (
                                    <div
                                      key={role}
                                      className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3 text-center"
                                    >
                                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">
                                        {role}
                                      </p>
                                      <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                                        {count}
                                      </p>
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          )}

                        {/* Members management */}
                        <OrgMemberManager
                          organization={selectedOrganization}
                          onChanged={() => loadOrganizationDetails(selectedOrganization.id)}
                        />

                        {/* Pending Invitations */}
                        {selectedOrganization.invitations &&
                          selectedOrganization.invitations.length > 0 && (
                            <div>
                              <h5 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                                Pending Invitations ({selectedOrganization.invitations.length})
                              </h5>
                              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                                  {selectedOrganization.invitations.map((invitation) => (
                                    <div
                                      key={invitation.id}
                                      className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700"
                                    >
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                            {invitation.email}
                                          </p>
                                          <p className="text-xs text-gray-500 dark:text-gray-400">
                                            Sent {formatDate(invitation.createdAt)} • Expires{' '}
                                            {formatDate(invitation.expiresAt)}
                                          </p>
                                        </div>
                                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200">
                                          {invitation.role}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        No organization data available
                      </div>
                    )}
                  </div>

                  {/* Modal Footer */}
                  <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-700 px-6 py-4 flex justify-end border-t border-gray-200 dark:border-gray-600">
                    <button
                      onClick={closeOrganizationModal}
                      className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Create / Edit Organization Modal */}
            <OrganizationFormModal
              isOpen={showOrgFormModal}
              onClose={() => {
                setShowOrgFormModal(false)
                setOrgFormTarget(null)
              }}
              onSubmit={handleSubmitOrganization}
              organization={orgFormTarget}
              existingTypes={organizationTypes}
            />
          </div>
        )}

        {activeTab === 'data' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Data Management
              </h2>
              <div className="flex items-center gap-3">
                <button onClick={() => setActiveTab('questions')} className="btn btn-secondary">
                  <Download className="w-4 h-4 mr-2" />
                  Export Questions
                </button>
                <button onClick={() => setShowImportModal(true)} className="btn btn-primary">
                  <Upload className="w-4 h-4 mr-2" />
                  Import Questions
                </button>
                <button
                  onClick={() => setShowSchoolImportModal(true)}
                  className="btn btn-secondary"
                >
                  <School className="w-4 h-4 mr-2" />
                  Import Schools
                </button>
              </div>
            </div>
            <ImportExportJobsPanel
              isInline={true}
              onOpenImport={() => setShowImportModal(true)}
              onOpenSchoolImport={() => setShowSchoolImportModal(true)}
              onNavigateToQuestions={() => setActiveTab('questions')}
              externalRefreshSignal={jobsRefreshSignal}
            />
          </div>
        )}

        {activeTab === 'invitations' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Invitation Management
              </h2>
              <button
                onClick={() => setShowInvitationForm(!showInvitationForm)}
                className="btn btn-primary"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Create Invitation
              </button>
            </div>

            {/* Create Invitation Modal */}
            {showInvitationForm && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full shadow-xl">
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Create Invitation
                    </h3>
                    <button
                      onClick={() => setShowInvitationForm(false)}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <form onSubmit={handleCreateInvitation}>
                    <div className="p-6 space-y-4">
                      <div>
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                        >
                          Email Address
                        </label>
                        <input
                          type="email"
                          id="email"
                          required
                          value={invitationForm.email}
                          onChange={(e) =>
                            setInvitationForm({ ...invitationForm, email: e.target.value })
                          }
                          className="input w-full"
                          placeholder="user@example.com"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label
                            htmlFor="startingLevel"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                          >
                            Starting Level
                          </label>
                          <select
                            id="startingLevel"
                            value={invitationForm.startingLevel}
                            onChange={(e) =>
                              setInvitationForm({
                                ...invitationForm,
                                startingLevel: parseInt(e.target.value)
                              })
                            }
                            className="input w-full"
                          >
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((level) => (
                              <option key={level} value={level}>
                                Level {level}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label
                            htmlFor="expiresAt"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                          >
                            Expires At (Optional)
                          </label>
                          <input
                            type="datetime-local"
                            id="expiresAt"
                            value={invitationForm.expiresAt}
                            onChange={(e) =>
                              setInvitationForm({ ...invitationForm, expiresAt: e.target.value })
                            }
                            className="input w-full"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => setShowInvitationForm(false)}
                        className="btn btn-secondary"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isCreatingInvitation}
                        className="btn btn-primary"
                      >
                        {isCreatingInvitation ? (
                          <>
                            <LoadingSpinner size="sm" />
                            Creating...
                          </>
                        ) : (
                          <>
                            <UserPlus className="w-4 h-4 mr-2" />
                            Create Invitation
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Invitations List */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    Invitations
                  </h3>
                  {/* Filters */}
                  <div className="flex flex-wrap items-center gap-3">
                    {/* Email Search */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search by email..."
                        value={invitationEmailSearch}
                        onChange={(e) => {
                          setInvitationEmailSearch(e.target.value)
                          setSelectedInvitationIds([])
                        }}
                        className="pl-9 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent w-48"
                      />
                    </div>
                    {/* Status Filter */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Status:</span>
                      <div className="flex rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
                        {[
                          { value: 'pending', label: 'Active' },
                          { value: 'accepted', label: 'Accepted' },
                          { value: 'expired', label: 'Expired' },
                          { value: 'archived', label: 'Archived' },
                          { value: 'all', label: 'All' }
                        ].map((option) => (
                          <button
                            key={option.value}
                            onClick={() => {
                              setInvitationStatusFilter(option.value)
                              setSelectedInvitationIds([])
                            }}
                            className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                              invitationStatusFilter === option.value
                                ? 'bg-primary-600 text-white'
                                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Role Filter */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Role:</span>
                      <select
                        value={invitationRoleFilter}
                        onChange={(e) => {
                          setInvitationRoleFilter(e.target.value)
                          setSelectedInvitationIds([])
                        }}
                        className="px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="all">All Roles</option>
                        <option value="student">Student</option>
                        <option value="member">Member</option>
                        <option value="guardian">Guardian</option>
                        <option value="coach">Coach</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                    {/* Date Range Filter */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Created:</span>
                      <input
                        type="date"
                        value={invitationDateFrom}
                        onChange={(e) => {
                          setInvitationDateFrom(e.target.value)
                          setSelectedInvitationIds([])
                        }}
                        className="px-2 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      <span className="text-sm text-gray-400">to</span>
                      <input
                        type="date"
                        value={invitationDateTo}
                        onChange={(e) => {
                          setInvitationDateTo(e.target.value)
                          setSelectedInvitationIds([])
                        }}
                        className="px-2 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Bulk Actions Bar */}
              {selectedInvitationIds.length > 0 && (
                <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4 flex items-center justify-between">
                  <span className="text-sm text-primary-700 dark:text-primary-300">
                    {selectedInvitationIds.length} invitation
                    {selectedInvitationIds.length > 1 ? 's' : ''} selected
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSelectedInvitationIds([])}
                      className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                    >
                      Clear Selection
                    </button>
                    <button
                      onClick={handleBulkActivateInvitations}
                      disabled={bulkActivateLoading}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded transition-colors disabled:opacity-50"
                    >
                      {bulkActivateLoading ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                      Re-activate
                    </button>
                    <button
                      onClick={handleBulkResendInvitations}
                      disabled={bulkResendLoading}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors disabled:opacity-50"
                    >
                      {bulkResendLoading ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      Resend Emails
                    </button>
                    <button
                      onClick={() => setBulkArchiveConfirm(true)}
                      disabled={bulkArchiveLoading}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm bg-amber-600 hover:bg-amber-700 text-white rounded transition-colors disabled:opacity-50"
                    >
                      {bulkArchiveLoading ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <Archive className="w-4 h-4" />
                      )}
                      Archive
                    </button>
                    <button
                      onClick={() => setBulkDeleteConfirm(true)}
                      disabled={bulkDeleteLoading}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 text-white rounded transition-colors disabled:opacity-50"
                    >
                      {bulkDeleteLoading ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                      Delete Selected
                    </button>
                  </div>
                </div>
              )}

              <div
                ref={invitesTableRef}
                className="flex flex-col min-h-[300px]"
                style={invitesTableFillHeight ? { height: invitesTableFillHeight } : undefined}
              >
                <div className="overflow-auto flex-1 min-h-0">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-3 text-left">
                          <button
                            onClick={toggleInvitationSelectAll}
                            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                          >
                            {(() => {
                              const selectableCount = filteredInvitations.length
                              return selectedInvitationIds.length === selectableCount &&
                                selectableCount > 0 ? (
                                <CheckSquare className="w-4 h-4 text-primary-600" />
                              ) : (
                                <Square className="w-4 h-4 text-gray-400" />
                              )
                            })()}
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Organization
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Role
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Created By
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Expires
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Emails
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredInvitations.length === 0 ? (
                        <tr>
                          <td colSpan={11} className="px-4 py-8 text-center">
                            <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                              {invitationEmailSearch
                                ? 'No Matching Invitations'
                                : invitationStatusFilter === 'pending'
                                  ? 'No Active Invitations'
                                  : 'No Invitations'}
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-4">
                              {invitationEmailSearch
                                ? `No invitations found matching "${invitationEmailSearch}".`
                                : invitationStatusFilter === 'pending'
                                  ? 'No pending invitations to display.'
                                  : invitationStatusFilter === 'all'
                                    ? "You haven't created any invitations yet."
                                    : `No ${invitationStatusFilter} invitations to display.`}
                            </p>
                            {(invitationStatusFilter !== 'all' ||
                              invitationEmailSearch ||
                              invitationRoleFilter !== 'all' ||
                              invitationDateFrom ||
                              invitationDateTo) &&
                              invitations.length > 0 && (
                                <button
                                  onClick={() => {
                                    setInvitationStatusFilter('all')
                                    setInvitationEmailSearch('')
                                    setInvitationRoleFilter('all')
                                    setInvitationDateFrom('')
                                    setInvitationDateTo('')
                                  }}
                                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                                >
                                  Clear filters
                                </button>
                              )}
                            {invitations.length === 0 && (
                              <button
                                onClick={() => setShowInvitationForm(true)}
                                className="btn btn-primary"
                              >
                                <UserPlus className="w-4 h-4 mr-2" />
                                Create Your First Invitation
                              </button>
                            )}
                          </td>
                        </tr>
                      ) : (
                        filteredInvitations.map((invitation) => {
                          const statusConfig = {
                            pending: {
                              label: 'Active',
                              className:
                                'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                            },
                            accepted: {
                              label: 'Accepted',
                              className:
                                'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                            },
                            expired: {
                              label: 'Expired',
                              className:
                                'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                            },
                            cancelled: {
                              label: 'Revoked',
                              className:
                                'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                            },
                            archived: {
                              label: 'Archived',
                              className:
                                'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                            }
                          }
                          const status =
                            statusConfig[invitation.displayStatus] || statusConfig.pending
                          const isPending = invitation.displayStatus === 'pending'

                          return (
                            <tr
                              key={invitation.id}
                              className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                                !isPending ? 'bg-gray-50/50 dark:bg-gray-800/50' : ''
                              }`}
                            >
                              {/* Checkbox */}
                              <td className="px-4 py-3">
                                <button
                                  onClick={() => toggleInvitationSelect(invitation.id)}
                                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                >
                                  {selectedInvitationIds.includes(invitation.id) ? (
                                    <CheckSquare className="w-4 h-4 text-primary-600" />
                                  ) : (
                                    <Square className="w-4 h-4 text-gray-400" />
                                  )}
                                </button>
                              </td>
                              {/* Email */}
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                  <span className="font-medium text-gray-900 dark:text-gray-100">
                                    {invitation.email}
                                  </span>
                                  {invitation.isAdmin && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                                      Admin
                                    </span>
                                  )}
                                </div>
                              </td>
                              {/* Name */}
                              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                {[invitation.metadata?.firstName, invitation.metadata?.lastName]
                                  .filter(Boolean)
                                  .join(' ') || '—'}
                              </td>
                              {/* Organization */}
                              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                {invitation.organization?.name || (
                                  <span className="text-gray-400 dark:text-gray-500">System</span>
                                )}
                              </td>
                              {/* Role */}
                              <td className="px-4 py-3">
                                {getInvitationRoleBadge(invitation.role)}
                              </td>
                              {/* Type */}
                              <td className="px-4 py-3">
                                {invitation.metadata?.invitationType === 'family' ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                                    Family
                                  </span>
                                ) : invitation.metadata?.source === 'waitlist' ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">
                                    Waitlist
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                                    Direct
                                  </span>
                                )}
                              </td>
                              {/* Status */}
                              <td className="px-4 py-3">
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${status.className}`}
                                >
                                  {status.label}
                                </span>
                              </td>
                              {/* Created By */}
                              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                {invitation.invitedBy?.name || invitation.invitedBy?.email || '—'}
                              </td>
                              {/* Expires */}
                              <td
                                className={`px-4 py-3 text-sm whitespace-nowrap cursor-help ${
                                  invitation.displayStatus === 'expired'
                                    ? 'text-red-600 dark:text-red-400'
                                    : 'text-gray-500 dark:text-gray-400'
                                }`}
                                title={`Expires: ${formatDate(invitation.expiresAt)} • Created: ${formatDate(invitation.createdAt)}`}
                              >
                                {getRelativeTime(invitation.expiresAt)}
                              </td>
                              {/* Emails Sent */}
                              <td className="px-4 py-3">
                                <button
                                  onClick={() => handleViewEmailEvents(invitation)}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-900/50 transition-colors"
                                  title="View email history"
                                >
                                  <Mail className="w-3 h-3 mr-1" />
                                  {invitation.emailEvents?.length || 1}
                                </button>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-1">
                                  {/* Copy link - only for pending */}
                                  {isPending && (
                                    <button
                                      onClick={() => handleCopyInvitationLink(invitation)}
                                      className="p-1.5 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors"
                                      title="Copy invitation link"
                                    >
                                      {copiedInvitationId === invitation.id ? (
                                        <Check className="w-4 h-4 text-green-600" />
                                      ) : (
                                        <Copy className="w-4 h-4" />
                                      )}
                                    </button>
                                  )}
                                  {/* Resend email - only for pending */}
                                  {isPending && (
                                    <button
                                      onClick={() =>
                                        setResendInvitationData({
                                          id: invitation.id,
                                          email: invitation.email
                                        })
                                      }
                                      className="p-1.5 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded transition-colors"
                                      title="Resend invitation email"
                                    >
                                      <Send className="w-4 h-4" />
                                    </button>
                                  )}
                                  {/* Extend - for expired invitations */}
                                  {invitation.displayStatus === 'expired' && (
                                    <button
                                      onClick={() => setExtendInvitationId(invitation.id)}
                                      className="p-1.5 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
                                      title="Extend invitation"
                                    >
                                      <RefreshCw className="w-4 h-4" />
                                    </button>
                                  )}
                                  {/* Delete/Revoke - for all statuses */}
                                  <button
                                    onClick={() =>
                                      setDeleteInvitationData({
                                        id: invitation.id,
                                        status: invitation.displayStatus
                                      })
                                    }
                                    className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                    title={isPending ? 'Revoke invitation' : 'Delete invitation'}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Delete/Revoke Invitation Confirm Dialog */}
            <ConfirmDialog
              isOpen={!!deleteInvitationData}
              onClose={() => setDeleteInvitationData(null)}
              onConfirm={() => {
                handleDeleteInvitation(deleteInvitationData?.id)
                setDeleteInvitationData(null)
              }}
              title={
                deleteInvitationData?.status === 'pending'
                  ? 'Revoke Invitation'
                  : 'Delete Invitation'
              }
              message={
                deleteInvitationData?.status === 'pending'
                  ? 'Are you sure you want to revoke this invitation? The recipient will no longer be able to use it.'
                  : 'Are you sure you want to delete this invitation? This action cannot be undone.'
              }
              confirmText={deleteInvitationData?.status === 'pending' ? 'Revoke' : 'Delete'}
              cancelText="Cancel"
            />

            {/* Resend Invitation Confirm Dialog */}
            <ConfirmDialog
              isOpen={!!resendInvitationData && !isResendingInvitation}
              onClose={() => setResendInvitationData(null)}
              onConfirm={handleResendInvitation}
              title="Resend Invitation"
              message={`Send another invitation email to ${resendInvitationData?.email || 'this recipient'}?`}
              confirmText="Send"
              cancelText="Cancel"
              confirmButtonClass="bg-purple-600 hover:bg-purple-700 text-white"
              icon={Send}
            />

            {/* Bulk Archive Confirm Dialog */}
            <ConfirmDialog
              isOpen={bulkArchiveConfirm}
              onClose={() => setBulkArchiveConfirm(false)}
              onConfirm={handleBulkArchiveInvitations}
              title="Archive Invitations"
              message={`Are you sure you want to archive ${selectedInvitationIds.length} invitation${selectedInvitationIds.length > 1 ? 's' : ''}? Archived invitations can be re-activated later.`}
              confirmText={bulkArchiveLoading ? 'Archiving...' : 'Archive All'}
              cancelText="Cancel"
              confirmButtonClass="bg-amber-600 hover:bg-amber-700 text-white"
              icon={Archive}
              iconBgClass="bg-amber-100 dark:bg-amber-900/20"
              iconClass="text-amber-600 dark:text-amber-400"
            />

            {/* Bulk Delete Confirm Dialog */}
            <ConfirmDialog
              isOpen={bulkDeleteConfirm}
              onClose={() => setBulkDeleteConfirm(false)}
              onConfirm={handleBulkDeleteInvitations}
              title="Delete Invitations"
              message={`Are you sure you want to delete ${selectedInvitationIds.length} invitation${selectedInvitationIds.length > 1 ? 's' : ''}? This action cannot be undone.`}
              confirmText={bulkDeleteLoading ? 'Deleting...' : 'Delete All'}
              cancelText="Cancel"
              icon={Trash2}
            />

            {/* Email Events Modal */}
            {emailEventsModalData && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full shadow-xl max-h-[80vh] flex flex-col">
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        Email History
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {emailEventsModalData.email}
                      </p>
                    </div>
                    <button
                      onClick={() => setEmailEventsModalData(null)}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="p-6 overflow-y-auto flex-1">
                    <div className="space-y-4">
                      {emailEventsModalData.events.map((event, index) => (
                        <div
                          key={index}
                          className={`p-4 rounded-lg border ${
                            event.emailSent
                              ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
                              : 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                event.type === 'initial'
                                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
                                  : event.type === 'manual_resend'
                                    ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300'
                                    : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                              }`}
                            >
                              {event.type === 'initial'
                                ? 'Initial'
                                : event.type === 'manual_resend'
                                  ? 'Manual Resend'
                                  : 'Auto Reminder'}
                            </span>
                            <span
                              className={`text-xs font-medium ${event.emailSent ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
                            >
                              {event.emailSent ? 'Sent' : 'Failed'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {new Date(event.timestamp).toLocaleString()}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                            By: {event.triggeredByUser?.name || event.triggeredBy}
                          </p>
                          {event.error && (
                            <p className="text-xs text-red-500 mt-1">Error: {event.error}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Extend Invitation Modal */}
            {extendInvitationId && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                      <RefreshCw className="w-5 h-5 text-green-500 mr-2" />
                      Extend Invitation
                    </h3>
                  </div>
                  <div className="px-6 py-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      Choose how long to extend this invitation:
                    </p>
                    <select
                      value={selectedExtendHours}
                      onChange={(e) => setSelectedExtendHours(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value={24}>24 hours</option>
                      <option value={48}>48 hours</option>
                      <option value={168}>7 days</option>
                      <option value={720}>30 days</option>
                    </select>
                  </div>
                  <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                    <button
                      onClick={() => {
                        setExtendInvitationId(null)
                        setSelectedExtendHours(168)
                      }}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleExtendInvitation}
                      disabled={isExtendingInvitation}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      {isExtendingInvitation && <LoadingSpinner size="sm" />}
                      Extend
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Lazy-loaded admin tab panels: only the open tab's chunk is fetched on demand. */}
        <Suspense fallback={<TabLoadingFallback />}>
          {/* Waitlist Tab */}
          {activeTab === 'waitlist' && <WaitlistTab />}

          {/* Referrals Tab */}
          {activeTab === 'referrals' && <ReferralsTab />}

          {/* Campaigns Tab */}
          {activeTab === 'campaigns' && <CampaignsTab />}

          {/* Feature Announcements Tab */}
          {activeTab === 'feature-announcements' && <FeatureAnnouncementsTab />}

          {/* Feedback Tab */}
          {activeTab === 'feedback' && <AdminFeedbackDashboard />}

          {/* Compliance Tab */}
          {activeTab === 'compliance' && <ComplianceAdminPanel />}

          {/* Audit Logs Tab */}
          {activeTab === 'audit-logs' && <AuditLogTab />}

          {/* Vaults Tab */}
          {activeTab === 'vaults' && <VaultsTab />}

          {/* Questions Tab */}
          {activeTab === 'questions' && <QuestionsTab />}

          {/* Challenge Types Tab */}
          {activeTab === 'challenge-types' && <ChallengeTypesTab />}

          {/* Knowledge Graph Tab */}
          {activeTab === 'knowledge-graph' && <KnowledgeGraphTab />}

          {/* Jobs Tab */}
          {activeTab === 'jobs' && <JobsTab />}

          {/* Email Management Tab */}
          {activeTab === 'email-management' && <EmailManagementTab />}

          {/* Queue Monitor Tab */}
          {activeTab === 'queue-monitor' && <QueueMonitorTab />}

          {/* FOMO Notifications Tab */}
          {activeTab === 'fomo' && <FomoConfigTab />}

          {/* AI Advisor Tab */}
          {activeTab === 'ai-advisor' && <AIAdvisorTab />}

          {/* Whitelabeling Tab */}
          {activeTab === 'whitelabeling' && <WhitelabelingTab />}

          {/* Licensing Tab */}
          {activeTab === 'licensing' && <LicensingTab />}

          {/* Feature Flags Tab */}
          {activeTab === 'feature-flags' && <FeatureFlagsTab />}

          {/* Database Query Tab - needs explicit height for Monaco editor */}
          {activeTab === 'database-query' && (
            <div className="h-[calc(100dvh-180px)] min-h-[600px]">
              <DatabaseQueryTab />
            </div>
          )}

          {/* Schools Tab */}
          {activeTab === 'schools' && <SchoolsTab />}
          {activeTab === 'coach-applications' && <CoachApplicationsTab />}
          {activeTab === 'user-tags' && <UserTagsManager />}

          {/* Events/Scheduling Tab */}
          {activeTab === 'events' && <EventsTab />}
          {activeTab === 'live-sessions' && <LiveSessionRecordingsTab />}

          {/* Sponsored Tutoring Consent/Waivers Tab */}
          {activeTab === 'consent-waivers' && <ConsentWaiversTab />}

          {/* Tutoring Pods (tutor_group organizations) Tab */}
          {activeTab === 'tutor-groups' && <TutorGroupsTab />}

          {/* Marketing Users Tab */}
          {activeTab === 'marketing-users' && <MarketingUsersTab />}

          {/* Marketing Dashboard (moved from root-level /marketing) */}
          {activeTab === 'marketing' && <MarketingDashboardContent embedded />}
        </Suspense>
      </div>

      {/* User Details Modal */}
      <UserDetailsModal
        isOpen={showUserDetailsModal}
        onClose={() => {
          setShowUserDetailsModal(false)
          setSelectedUserId(null)
        }}
        userId={selectedUserId}
        onImpersonate={(data) => {
          // Clear localStorage and redirect - app will re-fetch user from session
          localStorage.removeItem('auth-storage')
          window.location.href = '/'
        }}
      />

      {/* Online Users Modal */}
      <OnlineUsersModal
        isOpen={showOnlineUsersModal}
        onClose={() => setShowOnlineUsersModal(false)}
      />

      {/* Clone User Modal */}
      <CloneUserModal
        isOpen={!!userToClone}
        user={userToClone}
        onClose={() => setUserToClone(null)}
        onCloned={() => loadUsers(usersPagination.page)}
      />

      {/* Question Import Modal */}
      <QuestionImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportComplete={() => setJobsRefreshSignal((s) => s + 1)}
      />

      {/* School Import Modal */}
      <SchoolImportModal
        isOpen={showSchoolImportModal}
        onClose={() => setShowSchoolImportModal(false)}
        onImportComplete={() => setJobsRefreshSignal((s) => s + 1)}
      />

      {/* Disable User Confirmation Dialog */}
      {showDisableDialog && userToDisable && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                <UserX className="w-5 h-5 text-red-500 mr-2" />
                Disable User Account
              </h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                You are about to disable the account for <strong>{userToDisable.email}</strong>.
                This will prevent them from logging in.
              </p>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Reason for disabling (required)
              </label>
              <textarea
                value={disableReason}
                onChange={(e) => setDisableReason(e.target.value)}
                placeholder="Enter the reason for disabling this account..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-red-500 resize-none"
                rows={3}
              />
            </div>
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 flex justify-end space-x-3 rounded-b-lg">
              <button
                onClick={() => {
                  setShowDisableDialog(false)
                  setDisableReason('')
                  setUserToDisable(null)
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  toggleUserStatus(userToDisable.id, true, disableReason)
                  setShowDisableDialog(false)
                  setDisableReason('')
                  setUserToDisable(null)
                }}
                disabled={!disableReason.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Disable Account
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enable User Confirmation Dialog */}
      {showEnableDialog && userToEnable && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                <UserCheck className="w-5 h-5 text-green-500 mr-2" />
                Enable User Account
              </h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-600 dark:text-gray-400">
                Are you sure you want to enable the account for{' '}
                <strong>{userToEnable.email}</strong>?
              </p>
              {userToEnable.disabledReason && (
                <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <p className="text-sm text-red-700 dark:text-red-300">
                    <strong>Previously disabled for:</strong> {userToEnable.disabledReason}
                  </p>
                </div>
              )}
            </div>
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 flex justify-end space-x-3 rounded-b-lg">
              <button
                onClick={() => {
                  setShowEnableDialog(false)
                  setUserToEnable(null)
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  toggleUserStatus(userToEnable.id, false)
                  setShowEnableDialog(false)
                  setUserToEnable(null)
                }}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Enable Account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminDashboard
